import React, { useState, useRef, useEffect } from 'react';
import { Box, Button, IconButton, Grid } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { COLOR_PRIMARY_2 } from 'src/colors';
import { formatCurrencyBasedOnCountryCode } from 'src/utils/functions';
import TabBar from 'src/App/Components/Tabs/TabBar';

const CollapsibleDrawer = ({
  drawerHeight,
  setDrawerHeight,
  collapsed,
  setCollapsed,
  listingsCount,
  onClose,
  setMapHeightOnMobileView,
  overlayButton,
  isOverLayVisible = false,
  components,
  minDrawerHeight,
  maxDrawerHeight,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [currentTab, setCurrentTab] = useState<'overview' | 'listings'>('listings');
  const dragHandleRef = useRef(null);

  const mobileDrawerTabs = {
    overview: {
      label: 'Overview',
      value: 'overview',
      icon: null,
      onclick: () => {
        setCurrentTab('overview');
        setMapHeightOnMobileView(60);
      },
      height: '40vh',
    },
    listings: {
      label: 'Listings',
      value: 'listings',
      icon: null,
      onclick: () => {
        setCurrentTab('listings');
        setMapHeightOnMobileView(40);
      },
      height: '60vh',
    },
  };

  // Handle drag start
  const handleDragStart = e => {
    e.preventDefault();
    setIsDragging(true);
    setStartY(e.clientY || (e.touches && e.touches[0].clientY));
  };

  // Handle drag movement
  const handleDrag = e => {
    if (!isDragging) return;

    const clientY = e.clientY || (e.touches && e.touches[0].clientY);
    if (!clientY) return;

    const deltaY = startY - clientY;
    const newHeight = Math.min(
      maxDrawerHeight,
      Math.max(minDrawerHeight, drawerHeight + (deltaY / window.innerHeight) * 100)
    );

    setStartY(clientY);
    setDrawerHeight(newHeight);

    // Update the map height when drawer height changes
    // Map height = 100 - drawer height
    setMapHeightOnMobileView(100 - newHeight);
  };

  // Handle drag end
  const handleDragEnd = () => {
    setIsDragging(false);
  };

  // Add and remove event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDrag);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDrag);
      window.addEventListener('touchend', handleDragEnd);
    }

    return () => {
      window.removeEventListener('mousemove', handleDrag);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchmove', handleDrag);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, [isDragging, handleDrag]);

  // Update map height when drawer is expanded or collapsed
  useEffect(() => {
    if (collapsed) {
      setMapHeightOnMobileView(100);
    } else {
      setMapHeightOnMobileView(100 - drawerHeight);
    }
  }, [collapsed, drawerHeight]);

  if (collapsed) {
    return (
      <Button
        variant="contained"
        onClick={() => setCollapsed(false)}
        sx={{
          position: 'absolute',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '8px 16px',
          borderRadius: '20px',
          backgroundColor: COLOR_PRIMARY_2,
          color: 'white',
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.15)',
          '&:hover': {
            backgroundColor: COLOR_PRIMARY_2,
          },
          zIndex: 10,
          fontWeight: 'bold',
          textTransform: 'none',
        }}
      >
        See Listings (
        {listingsCount != null && listingsCount != undefined
          ? formatCurrencyBasedOnCountryCode(listingsCount, 'USD')
          : '...'}
        )
      </Button>
    );
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        left: 0,
        bottom: 0,
        width: '100%',
        height: `${drawerHeight}vh`,
        backgroundColor: '#fff',
        borderTopLeftRadius: '16px',
        borderTopRightRadius: '16px',
        boxShadow: '0px -4px 10px rgba(0, 0, 0, 0.1)',
        zIndex: 10,
        transition: isDragging ? 'none' : 'height 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Drag handle at the top of drawer */}
      <Box
        ref={dragHandleRef}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        sx={{
          height: '24px',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          cursor: 'ns-resize',
          borderBottom: '1px solid #eee',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            width: '40px',
            height: '4px',
            backgroundColor: '#ddd',
            borderRadius: '2px',
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            right: '10px',
            top: '0',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <IconButton size="small" onClick={() => setCollapsed(true)}>
            <KeyboardArrowDownIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Tab Bar */}
      <Box
        sx={{
          backgroundColor: '#fff',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <TabBar
          tabList={Object.values(mobileDrawerTabs)}
          selectedCurrentTab={currentTab}
          color={COLOR_PRIMARY_2}
          renderLabelCondition={() => true}
          actionButton={[]}
          showActionButton={false}
          transparentBackground={true}
        />
      </Box>

      {/* Drawer content */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          background: '#fff',
          width: '100%',
          pl: 2,
          pr: 2,
        }}
      >
        <Grid
          container
          direction="column"
          xs={12}
          spacing={2}
          justifyContent="flex-start"
          alignItems="flex-start"
        >
          {components &&
            components[currentTab] &&
            components[currentTab].map((component, index) => (
              <Grid item xs={12} key={index} sx={{ width: '95%' }}>
                {component}
              </Grid>
            ))}
        </Grid>
      </Box>

      {/* Overlay Button for Overview tab */}
      {currentTab === 'overview' && isOverLayVisible && overlayButton && (
        <Box
          sx={{
            position: 'absolute',
            bottom: '20px',
            right: '20px',
            zIndex: 11,
          }}
        >
          {overlayButton}
        </Box>
      )}
    </Box>
  );
};

export default CollapsibleDrawer;
